/**
 * Bloomyer Places — Carousel with drag, auto-scroll & pause on hover
 * Custom element: <bp-places>
 */

if (!customElements.get('bp-places')) {
  class BpPlaces extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.wrapper = null;
      this.cards = [];
      this.dots = [];
      this.currentIndex = 0;
      this.autoplay = false;
      this.autoplaySpeed = 4000;
      this.autoplayTimer = null;

      // Drag state
      this.isDragging = false;
      this.startX = 0;
      this.currentTranslate = 0;
      this.prevTranslate = 0;
      this.animationID = null;
    }

    connectedCallback() {
      this.track = this.querySelector('.bp-track');
      this.wrapper = this.querySelector('.bp-track-wrapper');
      this.cards = [...this.querySelectorAll('.bp-card')];
      this.dots = [...this.querySelectorAll('.bp-dot')];

      if (!this.track || this.cards.length === 0) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.speed, 10) || 4000;

      this.dotsContainer = this.querySelector('.bp-dots');

      this.initDrag();
      this.initHover();
      this.buildDots();

      if (this.autoplay) this.startAutoplay();

      this._onResize = this.handleResize.bind(this);
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this._onResize);
    }

    /* ---- Gap between cards (responsive) ---- */
    getGap() {
      const style = getComputedStyle(this.track);
      return parseFloat(style.columnGap || style.gap || '0') || 0;
    }

    /* ---- Visible cards count ---- */
    getVisibleCount() {
      if (!this.cards[0]) return 3;
      const cardW = this.cards[0].offsetWidth + this.getGap();
      const viewW = this.wrapper.offsetWidth;
      return Math.max(1, Math.round(viewW / cardW));
    }

    getMaxIndex() {
      const visible = this.getVisibleCount();
      return Math.max(0, this.cards.length - visible);
    }

    /* ---- Slide to index ---- */
    slideTo(index) {
      const max = this.getMaxIndex();
      this.currentIndex = Math.max(0, Math.min(index, max));

      const cardW = this.cards[0].offsetWidth + this.getGap();
      const offset = -this.currentIndex * cardW;

      this.track.classList.remove('no-transition');
      this.track.style.transform = `translateX(${offset}px)`;
      this.currentTranslate = offset;
      this.prevTranslate = offset;

      this.updateDots();
    }

    next() {
      const max = this.getMaxIndex();
      if (this.currentIndex >= max) {
        this.slideTo(0);
      } else {
        this.slideTo(this.currentIndex + 1);
      }
    }

    /* ---- Dots ---- */
    buildDots() {
      if (!this.dotsContainer) return;
      const pages = this.getMaxIndex() + 1;

      if (pages <= 1) {
        this.dotsContainer.innerHTML = '';
        this.dotsContainer.style.display = 'none';
        this.dots = [];
        return;
      }

      this.dotsContainer.style.display = '';
      this.dotsContainer.innerHTML = '';
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('span');
        dot.className = 'bp-dot' + (i === this.currentIndex ? ' is-active' : '');
        dot.addEventListener('click', () => this.slideTo(i));
        this.dotsContainer.appendChild(dot);
      }
      this.dots = [...this.dotsContainer.querySelectorAll('.bp-dot')];
    }

    updateDots() {
      if (!this.dots || this.dots.length === 0) return;
      const activeIdx = Math.max(0, Math.min(this.currentIndex, this.dots.length - 1));
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === activeIdx);
      });
    }

    /* ---- Autoplay ---- */
    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    /* ---- Hover pause ---- */
    initHover() {
      this.wrapper.addEventListener('mouseenter', () => {
        if (this.autoplay) this.stopAutoplay();
      });
      this.wrapper.addEventListener('mouseleave', () => {
        if (this.autoplay) this.startAutoplay();
      });
    }

    /* ---- Drag / Swipe ---- */
    initDrag() {
      // Mouse
      this.wrapper.addEventListener('mousedown', (e) => this.dragStart(e));
      this.wrapper.addEventListener('mousemove', (e) => this.dragMove(e));
      this.wrapper.addEventListener('mouseup', () => this.dragEnd());
      this.wrapper.addEventListener('mouseleave', () => {
        if (this.isDragging) this.dragEnd();
      });

      // Touch
      this.wrapper.addEventListener('touchstart', (e) => this.dragStart(e), { passive: true });
      this.wrapper.addEventListener('touchmove', (e) => this.dragMove(e), { passive: false });
      this.wrapper.addEventListener('touchend', () => this.dragEnd());

      // Prevent default drag on images
      this.track.querySelectorAll('img').forEach((img) => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });
    }

    getPositionX(e) {
      return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    }

    dragStart(e) {
      this.isDragging = true;
      this.startX = this.getPositionX(e);
      this.prevTranslate = this.currentTranslate;
      this.track.classList.add('no-transition');
      this.wrapper.classList.add('is-dragging');
      if (this.autoplay) this.stopAutoplay();
    }

    dragMove(e) {
      if (!this.isDragging) return;
      const currentX = this.getPositionX(e);
      const diff = currentX - this.startX;
      this.currentTranslate = this.prevTranslate + diff;
      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    }

    dragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.wrapper.classList.remove('is-dragging');

      const movedBy = this.currentTranslate - this.prevTranslate;
      const threshold = 80;

      if (movedBy < -threshold) {
        this.slideTo(this.currentIndex + 1);
      } else if (movedBy > threshold) {
        this.slideTo(this.currentIndex - 1);
      } else {
        this.slideTo(this.currentIndex);
      }

      // Don't restart autoplay here — mouseleave handler does it
    }

    /* ---- Resize ---- */
    handleResize() {
      this.buildDots();
      this.slideTo(this.currentIndex);
    }
  }

  customElements.define('bp-places', BpPlaces);
}
